import { createBrowserRouter, Navigate } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout/PublicLayout';
import StaffLayout from '../components/StaffLayout/StaffLayout';
import RequireRole from '../components/RequireRole/RequireRole';
import NotFoundPage from '../pages/NotFoundPage/NotFoundPage';

import PublicIndexPage from '../pages/public/PublicIndexPage';
import LandingPage from '../pages/public/LandingPage/LandingPage';
import SubscribePage from '../pages/public/SubscribePage/SubscribePage';
import UnsubscribeTokenPage from '../pages/public/UnsubscribeTokenPage/UnsubscribeTokenPage';
import SubscriberLoginPage from '../pages/public/SubscriberLoginPage/SubscriberLoginPage';
import MyGroupsPage from '../pages/public/MyGroupsPage/MyGroupsPage';
import SubscriptionsPage from '../pages/public/SubscriptionsPage/SubscriptionsPage';
import SettingsPage from '../pages/public/SettingsPage/SettingsPage';
import LoginPage from '../pages/auth/LoginPage/LoginPage';

import OrganizationsPage from '../pages/admin/OrganizationsPage';
import OrganizationCreatePage from '../pages/admin/OrganizationCreatePage';
import OrganizationEditPage from '../pages/admin/OrganizationEditPage';
import EmailDomainsPage from '../pages/admin/EmailDomainsPage';
import RequestsPage from '../pages/admin/RequestsPage';
import AdminGroupsPage from '../pages/admin/GroupsPage';
import GroupCreatePage from '../pages/admin/GroupCreatePage';
import GroupEditPage from '../pages/admin/GroupEditPage';
import GroupMessagesPage from '../pages/admin/GroupMessagesPage';
import GroupSubscribersPage from '../pages/admin/GroupSubscribersPage';
import GroupSubscriberCreatePage from '../pages/admin/GroupSubscriberCreatePage';
import SystemNotificationsPage from '../pages/admin/SystemNotificationsPage';
import ScheduledNotificationsPage from '../pages/admin/ScheduledNotificationsPage';
import SystemGroupCreatePage from '../pages/admin/SystemGroupCreatePage';
import SystemGroupEditPage from '../pages/admin/SystemGroupEditPage';
import SystemGroupSubscribersPage from '../pages/admin/SystemGroupSubscribersPage';
import SystemGroupSubscriberCreatePage from '../pages/admin/SystemGroupSubscriberCreatePage';
import UsersPage from '../pages/admin/UsersPage';
import UserCreatePage from '../pages/admin/UserCreatePage';
import UserEditPage from '../pages/admin/UserEditPage';
import UserPasswordPage from '../pages/admin/UserPasswordPage';
import AdminMetricsPage from '../pages/admin/MetricsPage';

import ManagerGroupsPage from '../pages/manager/GroupsPage';
import ManagerGroupEditPage from '../pages/manager/GroupEditPage';
import ManagerGroupSubscribersPage from '../pages/manager/GroupSubscribersPage';
import ManagerGroupMessagesPage from '../pages/manager/GroupMessagesPage';
import GroupRequestCreatePage from '../pages/manager/GroupRequestCreatePage';
import SubscribersPage from '../pages/manager/SubscribersPage';
import SubscriberCreatePage from '../pages/manager/SubscriberCreatePage';
import SubscriberEditPage from '../pages/manager/SubscriberEditPage';
import HostsPage from '../pages/manager/HostsPage';
import HostCreatePage from '../pages/manager/HostCreatePage';
import HostEditPage from '../pages/manager/HostEditPage';
import MetricsPage from '../pages/manager/MetricsPage';
import MetricsGroupSubscribersPage from '../pages/manager/MetricsGroupSubscribersPage';
import MetricsChannelDeliveriesPage from '../pages/manager/MetricsChannelDeliveriesPage';
import MetricsSubscriberDeliveriesPage from '../pages/manager/MetricsSubscriberDeliveriesPage';

import GiveawaysPage from '../pages/host/GiveawaysPage';
import GiveawayCreatePage from '../pages/host/GiveawayCreatePage';
import GiveawayServingsPage from '../pages/host/GiveawayServingsPage';
import ScheduledAlertsPage from '../pages/host/ScheduledAlertsPage';
import HostGroupMessagesPage from '../pages/host/GroupMessagesPage';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <PublicLayout />,
    children: [
      { index: true, element: <PublicIndexPage /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'o/:organizationSlug', element: <LandingPage /> },
      { path: 'o/:organizationSlug/g/:groupId', element: <SubscribePage /> },
      { path: 'o/:organizationSlug/my-groups', element: <MyGroupsPage /> },
      { path: 'o/:organizationSlug/subscriptions', element: <SubscriptionsPage /> },
      { path: 'o/:organizationSlug/settings', element: <SettingsPage /> },
      { path: 'subscriber/login', element: <SubscriberLoginPage /> },
      { path: 'my-groups', element: <MyGroupsPage /> },
      { path: 'subscriptions', element: <SubscriptionsPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'unsubscribe/:token', element: <UnsubscribeTokenPage /> },
      { path: ':groupId', element: <SubscribePage /> },
    ],
  },

  {
    path: '/admin',
    element: <RequireRole allow={['admin']} />,
    children: [
      {
        element: <StaffLayout />,
        children: [
          { index: true, element: <Navigate to="organizations" replace /> },
          { path: 'organizations', element: <OrganizationsPage /> },
          { path: 'organizations/create', element: <OrganizationCreatePage /> },
          { path: 'organizations/:id/edit', element: <OrganizationEditPage /> },
          { path: 'email-domains', element: <EmailDomainsPage /> },
          { path: 'requests', element: <RequestsPage /> },
          { path: 'groups', element: <AdminGroupsPage /> },
          { path: 'groups/create', element: <GroupCreatePage /> },
          { path: 'groups/:id/edit', element: <GroupEditPage /> },
          { path: 'groups/:id/messages', element: <GroupMessagesPage /> },
          { path: 'groups/:id/subscribers', element: <GroupSubscribersPage /> },
          { path: 'groups/:id/subscribers/create', element: <GroupSubscriberCreatePage /> },
          { path: 'system-notifications', element: <SystemNotificationsPage /> },
          { path: 'schedule', element: <ScheduledNotificationsPage /> },
          { path: 'system-groups/create', element: <SystemGroupCreatePage /> },
          { path: 'system-groups/:id/edit', element: <SystemGroupEditPage /> },
          { path: 'system-groups/:id/subscribers', element: <SystemGroupSubscribersPage /> },
          { path: 'system-groups/:id/subscribers/create', element: <SystemGroupSubscriberCreatePage /> },
          { path: 'users', element: <UsersPage /> },
          { path: 'users/create', element: <UserCreatePage /> },
          { path: 'users/:id/edit', element: <UserEditPage /> },
          { path: 'users/:id/password', element: <UserPasswordPage /> },
          { path: 'metrics', element: <AdminMetricsPage /> },
        ],
      },
    ],
  },

  {
    path: '/manager',
    element: <RequireRole allow={['manager']} />,
    children: [
      {
        element: <StaffLayout />,
        children: [
          { index: true, element: <Navigate to="groups" replace /> },
          { path: 'groups', element: <ManagerGroupsPage /> },
          { path: 'groups/requests/create', element: <GroupRequestCreatePage /> },
          { path: 'groups/:id/edit', element: <ManagerGroupEditPage /> },
          { path: 'groups/:id/subscribers', element: <ManagerGroupSubscribersPage /> },
          { path: 'groups/:id/messages', element: <ManagerGroupMessagesPage /> },
          { path: 'subscribers', element: <SubscribersPage /> },
          { path: 'subscribers/create', element: <SubscriberCreatePage /> },
          { path: 'subscribers/:id/edit', element: <SubscriberEditPage /> },
          { path: 'hosts', element: <HostsPage /> },
          { path: 'hosts/create', element: <HostCreatePage /> },
          { path: 'hosts/:id/edit', element: <HostEditPage /> },
          { path: 'metrics', element: <MetricsPage /> },
          { path: 'metrics/groups/:id/subscribers', element: <MetricsGroupSubscribersPage /> },
          { path: 'metrics/groups/:id/deliveries', element: <MetricsChannelDeliveriesPage /> },
          {
            path: 'metrics/groups/:id/subscribers/:subscriberId/deliveries',
            element: <MetricsSubscriberDeliveriesPage />,
          },
        ],
      },
    ],
  },

  {
    path: '/host',
    element: <RequireRole allow={['host']} />,
    children: [
      {
        element: <StaffLayout />,
        children: [
          { index: true, element: <Navigate to="giveaways" replace /> },
          { path: 'giveaways', element: <GiveawaysPage /> },
          { path: 'giveaways/create', element: <GiveawayCreatePage /> },
          { path: 'giveaways/:id/servings', element: <GiveawayServingsPage /> },
          { path: 'schedule', element: <ScheduledAlertsPage /> },
          { path: 'groups/:id/messages', element: <HostGroupMessagesPage /> },
        ],
      },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
]);
